import React, { createContext, useContext, useState, useEffect } from 'react';

const AccessibilityContext = createContext();

// Accessibility presets for common needs
export const ACCESSIBILITY_PRESETS = {
  default: {
    name: 'Standaard',
    fontSize: 'normal',
    contrast: 'normal',
    font: 'default',
    lineSpacing: 'normal',
    extraTime: 0
  },
  dyslexia: {
    name: 'Dyslexie-vriendelijk',
    fontSize: 'large',
    contrast: 'normal',
    font: 'dyslexia',
    lineSpacing: 'extra',
    extraTime: 30
  },
  lowVision: {
    name: 'Slechtziend',
    fontSize: 'xlarge',
    contrast: 'high',
    font: 'default',
    lineSpacing: 'extra',
    extraTime: 50
  },
  colorBlind: {
    name: 'Kleurenblind',
    fontSize: 'normal',
    contrast: 'colorblind',
    font: 'default',
    lineSpacing: 'normal',
    extraTime: 0
  }
};

// CSS class mappings
const FONT_SIZE_CLASSES = {
  normal: '',
  large: 'a11y-font-large',
  xlarge: 'a11y-font-xlarge'
};

const CONTRAST_CLASSES = {
  normal: '',
  high: 'a11y-high-contrast',
  colorblind: 'a11y-colorblind'
};

const FONT_CLASSES = {
  default: '',
  dyslexia: 'a11y-font-dyslexia'
};

const LINE_SPACING_CLASSES = {
  normal: '',
  extra: 'a11y-line-spacing-extra'
};

export function AccessibilityProvider({ children }) {
  // State for accessibility settings
  const [settings, setSettings] = useState({
    fontSize: 'normal',
    contrast: 'normal',
    font: 'default',
    lineSpacing: 'normal',
    extraTime: 0,
    reduceMotion: false,
    highlightFocus: false  // Off by default, user can enable if needed
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('testwisely-a11y');
    if (savedSettings) {
      try {
        setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
      } catch (e) {
        console.error('Error loading accessibility settings:', e);
      }
    }

    // Check for system preference for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setSettings(prev => ({ ...prev, reduceMotion: true }));
    }
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('testwisely-a11y', JSON.stringify(settings));
  }, [settings]);

  // Apply CSS classes to body
  useEffect(() => {
    const body = document.body;

    // Remove all existing a11y classes
    body.classList.forEach(cls => {
      if (cls.startsWith('a11y-')) {
        body.classList.remove(cls);
      }
    });

    // Add current classes
    const classes = [
      FONT_SIZE_CLASSES[settings.fontSize],
      CONTRAST_CLASSES[settings.contrast],
      FONT_CLASSES[settings.font],
      LINE_SPACING_CLASSES[settings.lineSpacing],
      settings.reduceMotion ? 'a11y-reduce-motion' : '',
      settings.highlightFocus ? 'a11y-highlight-focus' : ''
    ].filter(Boolean);

    classes.forEach(cls => body.classList.add(cls));

    return () => {
      classes.forEach(cls => body.classList.remove(cls));
    };
  }, [settings]);

  // Update a single setting
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Apply a preset
  const applyPreset = (presetKey) => {
    const preset = ACCESSIBILITY_PRESETS[presetKey];
    if (preset) {
      setSettings(prev => ({
        ...prev,
        fontSize: preset.fontSize,
        contrast: preset.contrast,
        font: preset.font,
        lineSpacing: preset.lineSpacing,
        extraTime: preset.extraTime
      }));
    }
  };

  // Reset to defaults
  const resetSettings = () => {
    setSettings({
      fontSize: 'normal',
      contrast: 'normal',
      font: 'default',
      lineSpacing: 'normal',
      extraTime: 0,
      reduceMotion: false,
      highlightFocus: false
    });
  };

  // Calculate adjusted time (for extended time)
  const getAdjustedTime = (baseTimeMinutes) => {
    const extraPercent = settings.extraTime / 100;
    return Math.ceil(baseTimeMinutes * (1 + extraPercent));
  };

  const value = {
    settings,
    updateSetting,
    applyPreset,
    resetSettings,
    getAdjustedTime,
    presets: ACCESSIBILITY_PRESETS
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
}

export default AccessibilityContext;
